import { getClient } from "src/api/api.ts";
import { HttpError } from "src/api/httpError.ts";
import { Customer, CustomerSaveRequest, CustomerSortBy } from "src/model/customer";
import { PageAndSortRequest } from "src/model/pageRequest.ts";
import { PaginatedResponse } from "src/model/paginatedResponse.ts";
import { SubscriptionStats } from "src/model/subscriptionStats";

export const fetchCustomers = async (
  pagination: PageAndSortRequest<CustomerSortBy>,
  onlyActive: boolean | undefined = undefined
): Promise<PaginatedResponse<Customer>> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().GET("/API/iSEA5/V2.1/Customers", {
    params: {
      query: {
        onlyActive: onlyActive,
        limit: pagination.limit,
        offset: pagination.offset,
        sortBy: pagination.sortBy,
        sortDir: pagination.sortDir,
      },
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to fetch customers");
};

export const fetchCompanies = async (
  customerId: string,
  onlyActive: boolean | undefined = undefined
): Promise<Customer[]> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().GET("/API/iSEA5/V2.1/Customers/{customerId}/Companies", {
    params: {
      path: {
        customerId: customerId,
      },
      query: {
        onlyActive: onlyActive,
      },
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to fetch companies");
};

export const fetchCustomerSubscriptionStats = async (customerId: string): Promise<SubscriptionStats> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().GET("/API/iSEA5/V2.1/Customers/{customerId}/Subscriptions/Stats", {
    params: {
      path: {
        customerId: customerId,
      },
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to fetch companies");
};

export const fetchCustomerDetails = async (customerId: string): Promise<Customer> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().GET("/API/iSEA5/V2.1/Customers/{customerId}", {
    params: {
      path: {
        customerId: customerId,
      },
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to fetch customer");
};

export const createCompany = async (customerId: string, customerSaveRequest: CustomerSaveRequest): Promise<void> => {
  const {
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/Customers/{customerId}/Companies", {
    params: {
      path: {
        customerId: customerId,
      },
    },
    body: customerSaveRequest,
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }
};

export const createCustomer = async (customerSaveRequest: CustomerSaveRequest): Promise<void> => {
  const {
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/Customers", {
    body: customerSaveRequest,
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }
};

export const updateCustomer = async (customerId: string, customerSaveRequest: CustomerSaveRequest): Promise<void> => {
  const {
    error,
    response: { status },
  } = await getClient().PUT("/API/iSEA5/V2.1/Customers/{customerId}", {
    params: {
      path: {
        customerId: customerId,
      },
    },
    body: customerSaveRequest,
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }
};
